// Feldauswahl je Nutzer für die linke Lead-Spalte (GET/PATCH /api/einstellungen/oberflaeche, users.settings):
//   ausgeblendet = Feld erscheint nie (auch nicht befüllt), immer = Feld steht auch leer da.
// Alles andere erscheint nur, wenn es ausgefüllt ist. Einmal je Sitzung geladen und für alle Leads geteilt.
import { useEffect, useState } from "react";
import { oberflaecheApi, type LeadFelderEinstellung } from "../../api";
import { useAuth } from "../../auth/AuthContext";

export type FeldModus = "immer" | "befuellt" | "nie";

const LEER: LeadFelderEinstellung = { ausgeblendet: [], immer: [] };

let fuerNutzer: string | null = null;
let stand: LeadFelderEinstellung = LEER;
let geladen = false;
let laden: Promise<void> | null = null;
const hoerer = new Set<() => void>();
const melden = () => hoerer.forEach((h) => h());

function holen(nutzerId: string): void {
  if (fuerNutzer !== nutzerId) {
    fuerNutzer = nutzerId;
    stand = LEER;
    geladen = false;
    laden = null;
  }
  if (geladen || laden) return;
  laden = oberflaecheApi
    .lesen()
    .then((r) => {
      if (fuerNutzer !== nutzerId) return;
      stand = { ...LEER, ...(r.settings?.lead_felder ?? LEER) };
      geladen = true;
    })
    .catch(() => {
      // Älterer Server oder Netzfehler: Standard (nur ausgefüllte Felder), beim nächsten Öffnen erneut versuchen
      if (fuerNutzer === nutzerId) laden = null;
    })
    .finally(melden);
}

export function modusVon(einstellung: LeadFelderEinstellung, feldId: string): FeldModus {
  if (einstellung.ausgeblendet.includes(feldId)) return "nie";
  if (einstellung.immer.includes(feldId)) return "immer";
  return "befuellt";
}

export function useFeldAuswahl() {
  const { nutzer } = useAuth();
  const [, setV] = useState(0);
  const id = nutzer?.id ?? "";
  useEffect(() => {
    const h = () => setV((v) => v + 1);
    hoerer.add(h);
    if (id) holen(id);
    return () => {
      hoerer.delete(h);
    };
  }, [id]);
  const einstellung = fuerNutzer === id ? stand : LEER;

  /** Modus eines Felds ändern; sofort sichtbar, dann gespeichert (bei Fehler zurück). */
  const setzen = async (feldId: string, modus: FeldModus): Promise<void> => {
    const vorher = stand;
    const neu: LeadFelderEinstellung = {
      ausgeblendet: vorher.ausgeblendet.filter((x) => x !== feldId).concat(modus === "nie" ? [feldId] : []),
      immer: vorher.immer.filter((x) => x !== feldId).concat(modus === "immer" ? [feldId] : []),
    };
    stand = neu;
    melden();
    try {
      const r = await oberflaecheApi.aendern({ lead_felder: neu });
      if (fuerNutzer === id) stand = { ...LEER, ...(r.settings?.lead_felder ?? neu) };
    } catch (e) {
      if (fuerNutzer === id) stand = vorher;
      throw e;
    } finally {
      melden();
    }
  };

  /** Alles auf Standard (nur ausgefüllte Felder). */
  const zuruecksetzen = async (): Promise<void> => {
    const vorher = stand;
    stand = LEER;
    melden();
    try {
      await oberflaecheApi.aendern({ lead_felder: LEER });
    } catch (e) {
      if (fuerNutzer === id) stand = vorher;
      throw e;
    } finally {
      melden();
    }
  };

  return { einstellung, setzen, zuruecksetzen };
}
